/**
 * The product title as it is printed on a label. Pure.
 *
 * A title is longer than a 1 in label is wide, and what tells two variants of a
 * product apart is usually at the END of the title (the grip size, the color,
 * the pack count), while what names the product is at the start. So the title
 * is cut in the MIDDLE, keeping both ends — the way Amazon's own FNSKU labels
 * print it — never clipped on the right.
 */

/** What joins the two kept ends. Three ASCII periods: the PDF's built-in font has them. */
export const ELLIPSIS = '...';

/**
 * Makes a title safe for the PDF's built-in Helvetica (WinAnsi encoding), which
 * cannot encode most of Unicode and would otherwise fail the whole document:
 * typographic quotes and dashes become their ASCII forms, the trademark signs
 * are dropped, accented Latin-1 letters stay, anything else left over is
 * dropped, and runs of whitespace collapse to one space.
 */
export function pdfSafeText(text: string): string {
  return text
    .replace(/[\u2018\u2019\u201a\u2032]/g, "'")
    .replace(/[\u201c\u201d\u201e\u2033]/g, '"')
    .replace(/[\u2010-\u2015\u2212]/g, '-')
    .replace(/\u2026/g, ELLIPSIS)
    .replace(/[\u00ae\u2122\u2120]/g, '')
    .replace(/[^\x20-\x7e\u00a1-\u00ff]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Fits a text into `maxWidth` by cutting out its middle. `measure` returns a
 * string's width in the same unit as `maxWidth` (the PDF font's own metrics, so
 * the result is exact for a proportional font).
 *
 * The text is returned unchanged when it fits. Otherwise the longest possible
 * head and tail are kept around an ellipsis, the head getting the odd character,
 * with no space left dangling against the ellipsis. When not even one character
 * each side fits, the text is cut from the right as a last resort; when nothing
 * fits at all the result is empty.
 */
export function truncateMiddle(
  text: string,
  maxWidth: number,
  measure: (text: string) => number,
): string {
  if (measure(text) <= maxWidth) return text;
  const chars = Array.from(text);
  const join = (kept: number) => {
    const head = Math.ceil(kept / 2);
    const tail = kept - head;
    const start = chars.slice(0, head).join('').trimEnd();
    const end =
      tail > 0
        ? chars
            .slice(chars.length - tail)
            .join('')
            .trimStart()
        : '';
    return `${start}${ELLIPSIS}${end}`;
  };

  // Widths grow with the characters kept, so the largest count that fits is
  // found by bisection.
  let low = 0;
  let high = chars.length - 1;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (measure(join(mid)) <= maxWidth) low = mid;
    else high = mid - 1;
  }
  if (low >= 2 && measure(join(low)) <= maxWidth) return join(low);

  // Too narrow for a head and a tail: keep what fits of the start.
  for (let kept = Math.min(low + 1, chars.length); kept >= 1; kept -= 1) {
    const cut = `${chars.slice(0, kept).join('').trimEnd()}${ELLIPSIS}`;
    if (measure(cut) <= maxWidth) return cut;
  }
  return '';
}
